import React, { useState, useMemo, useEffect } from "react";
import DownloadExcelFromData from "./DownloadExcelFromData";

const DynamicTable = ({ data, headers, rowsPerPage, maxWidth = 0 }) => {
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const columnOrder = Object.entries(headers).map(([key, value]) => key);
  // Detect if value is a date
  useEffect(() => {
    setPage(1);
  }, []);
  const isDate = (value) => {
    return !isNaN(new Date(value).getTime());
  };

  // Detect time (HH:mm or HH:mm:ss)
  const isTime = (value) => {
    return typeof value === "string" && /^\d{1,2}:\d{2}(:\d{2})?$/.test(value);
  };

  // Convert time to sortable number (seconds)
  const timeToSeconds = (timeStr) => {
    let parts = timeStr?.split(":").map(Number);
    if (parts.length === 2) parts.push(0); // HH:mm → HH:mm:00
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  };

  // Sorting logic
  const sortedData = useMemo(() => {
    let sortable = [...data];

    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let x = a[sortConfig.key];
        let y = b[sortConfig.key];

        // Number detection
        if (!isNaN(x) && !isNaN(y)) {
          x = Number(x);
          y = Number(y);
        }
        // Date detection
        else if (isDate(x) && isDate(y)) {
          x = new Date(x).getTime();
          y = new Date(y).getTime();
        }
        // Time detection
        else if (isTime(x) && isTime(y)) {
          x = timeToSeconds(x);
          y = timeToSeconds(y);
        }
        // Default string compare
        else {
          x = String(x).toLowerCase();
          y = String(y).toLowerCase();
        }

        if (x < y) return sortConfig.direction === "asc" ? -1 : 1;
        if (x > y) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return sortable;
  }, [data, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const pageData = sortedData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Handle sorting click
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Jump to page
  const handleJump = (e) => {
    if (e.key !== "Enter") return;
    const value = parseInt(e.target.value, 10);
    if (!value) return;
    setPage(Math.min(Math.max(1, value), totalPages));
  };
  const jumpToPage = () => {
    const value = parseInt(pageInput, 10);
    if (!value) return;
    setPage(Math.min(Math.max(1, value), totalPages));
  };

  return (
    <div className="newUiBody">
      <div className="table-responsive shadow-sm mt-2">
        <table className="table newUiTable table-bordered table-hover table-striped align-middle">
          <thead className="table-dark text-center ">
            <tr className="">
              <th>Sr</th>
              {Object.keys(headers).map((key) => (
                <th
                  key={key}
                  className="text-center text-wrap "
                  style={{
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => handleSort(key)}
                >
                  {headers[key]}

                  {/* Sort Indicator */}
                  {sortConfig.key === key && (
                    <span className="ms-2">
                      {sortConfig.direction === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {pageData.length > 0 ? (
              pageData.map((row, i) => (
                <tr key={i} className="text-center ">
                  <td> {i + 1 + (page - 1) * rowsPerPage}</td>
                  {Object.keys(headers).map((key) => {
                    return (
                      <td
                        key={key}
                        className="text-wrap"
                        style={{
                          maxWidth: row[key]?.length >= 100 && "550px",
                        }}
                      >
                        {typeof row[key] === "string" &&
                        row[key].length >= 20 && // for safety
                        row[key][10] === "T" &&
                        row[key].endsWith("Z")
                          ? row[key].slice(0, 10)
                          : row[key] || "-"}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  className="text-center py-3"
                  colSpan={Object.keys(headers).length}
                >
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <button
            className="btn btn-outline-primary me-2"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            ← Prev
          </button>

          <button
            className="btn btn-outline-primary"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next →
          </button>
        </div>

        <div>
          <strong>
            Page {page} of {totalPages}
          </strong>
        </div>

        <div className="input-group" style={{ width: "150px" }}>
          <input
            type="number"
            className="form-control"
            placeholder="Jump to..."
            onKeyDown={handleJump}
            max={totalPages}
            value={pageInput}
            onChange={(e) =>
              e.target.value <= totalPages && setPageInput(e.target.value)
            }
          />
          <button onClick={jumpToPage} className="input-group-text">
            Go
          </button>
        </div>
      </div>
      <DownloadExcelFromData
        data={data}
        columnOrder={columnOrder}
        fileName="download - Railsetu"
      />
    </div>
  );
};

export default DynamicTable;
